import React, { useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../../components/ui/PageHeader';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Tag } from 'primereact/tag';
import { GastoService } from '../../services/GastoService';

const Gastos = () => {
    const toast = useRef(null);
    const [loading, setLoading] = useState(false);
    const [gastos, setGastos] = useState([]);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [filtroCategoria, setFiltroCategoria] = useState(null);
    const [filtroMetodo, setFiltroMetodo] = useState(null);
    const [form, setForm] = useState({
        fecha: new Date(),
        concepto: '',
        categoria: 'otros',
        proveedor: '',
        numeroFactura: '',
        monto: 0,
        metodoPago: 'efectivo',
        observaciones: ''
    });

    const categorias = [
        { label: 'Utiles', value: 'utiles' },
        { label: 'Limpieza', value: 'limpieza' },
        { label: 'Servicios', value: 'servicios' },
        { label: 'Alimentacion', value: 'alimentacion' },
        { label: 'Otros', value: 'otros' }
    ];

    const metodosPago = [
        { label: 'Efectivo', value: 'efectivo' },
        { label: 'Transferencia', value: 'transferencia' },
        { label: 'Tarjeta', value: 'tarjeta' }
    ];

    useEffect(() => {
        loadGastos();
    }, []);

    const loadGastos = async () => {
        setLoading(true);
        try {
            const response = await GastoService.getAll();
            setGastos(response?.data || []);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar gastos', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const gastosFiltrados = useMemo(() => {
        return (gastos || []).filter((g) => {
            const okCategoria = filtroCategoria ? g.categoria === filtroCategoria : true;
            const okMetodo = filtroMetodo ? g.metodoPago === filtroMetodo : true;
            return okCategoria && okMetodo;
        });
    }, [gastos, filtroCategoria, filtroMetodo]);

    const totalGastos = useMemo(() => {
        return gastosFiltrados.reduce((acc, g) => acc + Number(g.monto || 0), 0);
    }, [gastosFiltrados]);

    const openNuevo = () => {
        setSubmitted(false);
        setForm({
            fecha: new Date(),
            concepto: '',
            categoria: 'otros',
            proveedor: '',
            numeroFactura: '',
            monto: 0,
            metodoPago: 'efectivo',
            observaciones: ''
        });
        setDialogVisible(true);
    };

    const saveGasto = async () => {
        setSubmitted(true);
        if (!form.concepto?.trim() || !form.categoria || !form.metodoPago || !form.monto || form.monto <= 0) {
            return;
        }

        try {
            const payload = {
                ...form,
                fecha: form.fecha ? new Date(form.fecha).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
            };
            await GastoService.create(payload);
            toast.current.show({ severity: 'success', summary: 'Exito', detail: 'Gasto registrado', life: 3000 });
            setDialogVisible(false);
            loadGastos();
        } catch (error) {
            const detail = error?.response?.data?.message || 'No se pudo registrar el gasto';
            toast.current.show({ severity: 'error', summary: 'Error', detail, life: 3500 });
        }
    };

    const montoBody = (row) => `S/ ${Number(row.monto || 0).toFixed(2)}`;

    const fechaBody = (row) => {
        if (!row.fecha) return '-';
        return new Date(row.fecha).toLocaleDateString();
    };

    const categoriaBody = (row) => <Tag value={(row.categoria || '').toUpperCase()} severity="info" />;
    const metodoBody = (row) => <Tag value={(row.metodoPago || '').toUpperCase()}  />;

    const toolbarLeft = () => (
        <div className="flex gap-2 align-items-center">
            <Button label="Nuevo Gasto" icon="pi pi-plus" onClick={openNuevo} />
            <Dropdown value={filtroCategoria} options={categorias} onChange={(e) => setFiltroCategoria(e.value)} placeholder="Filtrar categoria" showClear className="w-14rem" />
            <Dropdown value={filtroMetodo} options={metodosPago} onChange={(e) => setFiltroMetodo(e.value)} placeholder="Filtrar metodo" showClear className="w-14rem" />
        </div>
    );

    const toolbarRight = () => <div className="font-semibold">Total: S/ {totalGastos.toFixed(2)}</div>;

    const dialogFooter = (
        <div>
            <Button label="Cancelar" icon="pi pi-times" outlined onClick={() => setDialogVisible(false)} />
            <Button label="Guardar" icon="pi pi-check" onClick={saveGasto} />
        </div>
    );

    return (
        <div className="grid">
            <div className="col-12">
                <PageHeader 
                    title="Control de Gastos" 
                    description="Gestione los diferentes egresos que realizan las sucursales." 
                    icon="pi pi-money-bill" 
                />
                <div className="card">
                    <Toast ref={toast} />
                    <Toolbar className="mb-3" left={toolbarLeft} right={toolbarRight} />
                    <DataTable value={gastosFiltrados} loading={loading} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} responsiveLayout="scroll"
                        emptyMessage="No hay gastos registrados.">
                        <Column field="fecha" header="Fecha" body={fechaBody} sortable style={{ minWidth: '10rem' }} />
                        <Column field="concepto" header="Concepto" sortable style={{ minWidth: '16rem' }} />
                        <Column field="categoria" header="Categoria" body={categoriaBody} sortable style={{ minWidth: '10rem' }} />
                        <Column field="metodoPago" header="Metodo" body={metodoBody} sortable style={{ minWidth: '10rem' }} />
                        <Column field="monto" header="Monto" body={montoBody} sortable style={{ minWidth: '8rem' }} />
                        <Column field="proveedor" header="Proveedor" style={{ minWidth: '12rem' }} />
                        <Column field="observaciones" header="Observaciones" style={{ minWidth: '14rem' }} />
                    </DataTable>
                </div>
            </div>

            <Dialog visible={dialogVisible} style={{ width: '560px' }} header="Nuevo Gasto" modal footer={dialogFooter} onHide={() => setDialogVisible(false)}>
                <div className="field mb-3">
                    <label htmlFor="fecha">Fecha</label>
                    <Calendar id="fecha" value={form.fecha} onChange={(e) => setForm((prev) => ({ ...prev, fecha: e.value }))} dateFormat="dd/mm/yy" className="w-full" />
                </div>
                <div className="field mb-3">
                    <label htmlFor="concepto">Concepto</label>
                    <InputText id="concepto" value={form.concepto} onChange={(e) => setForm((prev) => ({ ...prev, concepto: e.target.value }))} className={submitted && !form.concepto?.trim() ? 'p-invalid w-full' : 'w-full'} />
                    {submitted && !form.concepto?.trim() && <small className="p-error">El concepto es requerido.</small>}
                </div>
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <label htmlFor="categoria">Categoria</label>
                        <Dropdown id="categoria" value={form.categoria} options={categorias} onChange={(e) => setForm((prev) => ({ ...prev, categoria: e.value }))} className="w-full" />
                    </div>
                    <div className="col-12 md:col-6">
                        <label htmlFor="metodoPago">Metodo de Pago</label>
                        <Dropdown id="metodoPago" value={form.metodoPago} options={metodosPago} onChange={(e) => setForm((prev) => ({ ...prev, metodoPago: e.value }))} className="w-full" />
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <label htmlFor="monto">Monto</label>
                        <InputNumber id="monto" value={form.monto} mode="currency" currency="PEN" locale="es-PE" onValueChange={(e) => setForm((prev) => ({ ...prev, monto: e.value || 0 }))} className={submitted && (!form.monto || form.monto <= 0) ? 'p-invalid w-full' : 'w-full'} />
                    </div>
                    <div className="col-12 md:col-6">
                        <label htmlFor="proveedor">Proveedor</label>
                        <InputText id="proveedor" value={form.proveedor} onChange={(e) => setForm((prev) => ({ ...prev, proveedor: e.target.value }))} className="w-full" />
                    </div>
                </div>
                <div className="field mb-3">
                    <label htmlFor="numeroFactura">Numero de Factura</label>
                    <InputText id="numeroFactura" value={form.numeroFactura} onChange={(e) => setForm((prev) => ({ ...prev, numeroFactura: e.target.value }))} className="w-full" />
                </div>
                <div className="field">
                    <label htmlFor="observaciones">Observaciones</label>
                    <InputTextarea id="observaciones" rows={3} value={form.observaciones} onChange={(e) => setForm((prev) => ({ ...prev, observaciones: e.target.value }))} className="w-full" />
                </div>
            </Dialog>
        </div>
    );
};

export default Gastos;
